// @section 1 OpsPulse route — daily ops pulse + AI review
// Reads GET /api/hq/ops-pulse (serving.ops_pulse_daily + latest
// serving.ops_pulse_review on internal_ops). Deterministic numbers come from
// the nightly caj-ops-pulse builder; the brief is the structured-outputs-only
// AI review. This page renders, it does not derive.

import { useEffect, useMemo, useState } from 'react';
import { Sparkline } from '@navipoint/ui';
import { getOpsPulse, type OpsPulseDay, type OpsPulsePayload, type InitiativeBreakdownRow } from '../api';

// @subsection 1.1 Lens + severity display metadata
const LENS_META: Record<string, { label: string; color: string }> = {
  revenue_direct: { label: 'Revenue direct', color: 'var(--mt-ok, #4ade80)' },
  product_leverage: { label: 'Product leverage', color: 'var(--mt-accent, #60a5fa)' },
  internal_meta: { label: 'Internal meta', color: 'var(--mt-watch, #fbbf24)' },
  other: { label: 'Other', color: '#6b7280' },
};
const LENS_ORDER = ['revenue_direct', 'product_leverage', 'internal_meta', 'other'];

const SEVERITY_CLASS: Record<string, string> = {
  alert: 'border-stall/50 text-stall',
  watch: 'border-watch/50 text-watch',
  info: 'border-paper-100/20 text-muted',
};

// @subsection 1.2 Helpers

function pct(v: number | null | undefined): string {
  return v == null ? 'n/a' : `${Math.round(v * 100)}%`;
}

function usd(v: number | null | undefined): string {
  return v == null ? 'n/a' : `$${Math.round(v).toLocaleString()}`;
}

/** Sparkline feed: nulls dropped (unscored days), zeros kept. */
function series(days: OpsPulseDay[], pick: (d: OpsPulseDay) => number | null | undefined): number[] {
  return days.map(pick).filter((v): v is number => v != null);
}

/** Minimal markdown for the brief: ## headings, **bold**, tables/lists as-is. */
function BriefMd({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <div className="text-sm leading-relaxed text-paper-100/90 space-y-1">
      {lines.map((line, i) => {
        if (line.startsWith('## '))
          return <div key={i} className="text-[11px] uppercase tracking-[2px] text-muted pt-3">{line.slice(3)}</div>;
        if (line.startsWith('|'))
          return <div key={i} className="font-mono text-xs text-paper-100/70 whitespace-pre">{line}</div>;
        const parts = line.split('**');
        return (
          <div key={i} className="whitespace-pre-wrap">
            {parts.map((p, j) => (j % 2 === 1 ? <strong key={j} className="text-paper-100">{p}</strong> : p))}
          </div>
        );
      })}
    </div>
  );
}

// @section 2 Page component

export default function OpsPulse({ embedded = false }: { embedded?: boolean } = {}) {
  const [data, setData] = useState<OpsPulsePayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  // @block 2.1 Load on mount
  useEffect(() => {
    getOpsPulse(28).then(setData).catch((e) => setError(String(e)));
  }, []);

  const days = data?.days ?? [];
  const latest = days.length ? days[days.length - 1] : null;

  // @block 2.2 Window lens totals (stacked bar data)
  const lensTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const d of days)
      for (const [k, v] of Object.entries(d.costByLens ?? {}))
        totals[k] = (totals[k] ?? 0) + (typeof v === 'number' ? v : 0);
    const sum = Object.values(totals).reduce((a, b) => a + b, 0);
    return { totals, sum };
  }, [days]);

  const kpis: { label: string; value: string; values: number[]; sub?: string }[] = latest
    ? [
        { label: 'Goal rate', value: pct(latest.goalRate), values: series(days, (d) => d.goalRate), sub: `latest scored day` },
        { label: 'Ship rate', value: pct(latest.shipRate), values: series(days, (d) => d.shipRate) },
        { label: 'Recorded cost', value: usd(latest.recordedCostUsd), values: series(days, (d) => d.recordedCostUsd), sub: 'shadow $, not cash' },
        { label: 'Sessions', value: String(latest.sessions ?? 'n/a'), values: series(days, (d) => d.sessions) },
        { label: 'Marathons (>15 turns)', value: String(latest.marathonSessions ?? 0), values: series(days, (d) => d.marathonSessions) },
        { label: 'Rework pairs', value: String(latest.reworkPairsNew ?? 0), values: series(days, (d) => d.reworkPairsNew) },
        { label: 'Commits', value: String(latest.githubCommits ?? 0), values: series(days, (d) => d.githubCommits) },
        { label: 'Drift open', value: latest.driftOpen == null ? 'n/a' : latest.driftOpen.toLocaleString(), values: [] },
      ]
    : [];

  return (
    <div className={embedded ? '' : 'md:-mx-10 md:-my-8'}>
      <section className={embedded ? 'text-paper-100 font-sans' : 'bg-ink-950 text-paper-100 min-h-screen font-sans'}>
        <div className={embedded ? 'max-w-[1400px] mx-auto' : 'px-4 py-5 md:px-10 md:py-7 max-w-[1400px] mx-auto'}>
          {/* @block 2.3 Header (hidden when embedded in the Command module) */}
          {!embedded && (
            <>
              <div className="flex items-center justify-between text-xs mb-6">
                <div className="text-muted">
                  <span className="text-paper-100">Atlas</span>
                  <span className="opacity-40"> / </span>
                  <span className="text-paper-100/80">Ops Pulse</span>
                </div>
                {latest && <div className="text-muted">latest complete day {latest.day}</div>}
              </div>

              <div className="mb-8">
                <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">Deterministic pulse + AI review</div>
                <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-paper-100">Ops Pulse</h1>
              </div>
            </>
          )}

          {error && (
            <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-4 mb-6 text-stall text-sm">{error}</div>
          )}
          {!data && !error && <em className="text-muted">Loading…</em>}

          {latest && (
            <>
              {/* @block 2.4 KPI strip with sparklines */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {kpis.map((k) => (
                  <div key={k.label} className="rounded-xl border border-paper-100/10 bg-ink-900 p-4">
                    <div className="text-[11px] uppercase tracking-[2px] text-muted mb-1">{k.label}</div>
                    <div className="text-2xl font-bold text-paper-100 mb-2">{k.value}</div>
                    {k.values.length > 1 && <Sparkline values={k.values} options={{ height: 28 }} />}
                    {k.sub && <div className="text-[10px] text-muted mt-1">{k.sub}</div>}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* @block 2.5 AI brief + flags */}
                <div className="rounded-xl border border-paper-100/10 bg-ink-900 p-5">
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="text-[11px] uppercase tracking-[2px] text-muted">AI review</div>
                    {data?.review && (
                      <div className="text-[10px] text-muted">
                        {data.review.day} · {data.review.model ?? 'model n/a'}
                      </div>
                    )}
                  </div>
                  {data?.review ? (
                    <>
                      {(data.review.flags ?? []).length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {data.review.flags!.map((f, i) => (
                            <span key={i}
                              className={`text-[11px] px-2 py-0.5 rounded-full border ${SEVERITY_CLASS[f.severity] ?? SEVERITY_CLASS.info}`}
                              title={f.note}>
                              {f.area}
                            </span>
                          ))}
                        </div>
                      )}
                      <BriefMd text={data.review.briefMd} />
                    </>
                  ) : (
                    <div className="text-sm text-muted">No review yet. The Ops-Pulse-Review task writes one daily at 06:15 AZ.</div>
                  )}
                </div>

                <div className="space-y-6">
                  {/* @block 2.6 Cost by lens (window total, stacked) */}
                  <div className="rounded-xl border border-paper-100/10 bg-ink-900 p-5">
                    <div className="text-[11px] uppercase tracking-[2px] text-muted mb-3">
                      Cost by lens · {days.length}d window
                    </div>
                    {lensTotals.sum > 0 ? (
                      <>
                        <div className="flex h-4 rounded overflow-hidden mb-3">
                          {LENS_ORDER.filter((k) => lensTotals.totals[k]).map((k) => (
                            <div key={k}
                              style={{ width: `${(lensTotals.totals[k] / lensTotals.sum) * 100}%`, background: LENS_META[k].color }}
                              title={`${LENS_META[k].label}: ${usd(lensTotals.totals[k])}`} />
                          ))}
                        </div>
                        <div className="space-y-1">
                          {LENS_ORDER.filter((k) => lensTotals.totals[k]).map((k) => (
                            <div key={k} className="flex items-center justify-between text-xs">
                              <span className="flex items-center gap-2">
                                <span className="inline-block w-2 h-2 rounded-sm" style={{ background: LENS_META[k].color }} />
                                <span className="text-paper-100/80">{LENS_META[k].label}</span>
                              </span>
                              <span className="text-muted">
                                {usd(lensTotals.totals[k])} · {Math.round((lensTotals.totals[k] / lensTotals.sum) * 100)}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-muted">No lens data in window.</div>
                    )}
                  </div>

                  {/* @block 2.7 Allocation vs target (latest 14d snapshot) */}
                  <div className="rounded-xl border border-paper-100/10 bg-ink-900 p-5">
                    <div className="text-[11px] uppercase tracking-[2px] text-muted mb-3">Allocation vs target · 14d</div>
                    {latest.allocationVsTarget?.length ? (
                      <div className="space-y-2">
                        {latest.allocationVsTarget.map((a) => (
                          <div key={a.bucket} className="text-xs">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-paper-100/80">{a.bucket}</span>
                              <span className={a.delta_pp > 2 ? 'text-watch' : a.delta_pp < -2 ? 'text-stall' : 'text-muted'}>
                                {a.actual_pct}% vs {a.target_pct}% ({a.delta_pp > 0 ? '+' : ''}{a.delta_pp}pp)
                              </span>
                            </div>
                            <div className="h-1.5 bg-ink-950 rounded overflow-hidden">
                              <div className="h-full bg-paper-100/40 rounded"
                                style={{ width: `${Math.min(100, Math.max(0, a.actual_pct))}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-muted">No allocation snapshot on the latest day.</div>
                    )}
                  </div>
                </div>
              </div>

              {/* @block 2.8 Initiative breakdown (cost per objective, incl. unattributed) */}
              {data?.initiativeBreakdown?.length ? (
                <div className="rounded-xl border border-paper-100/10 bg-ink-900 p-5 mb-8">
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="text-[11px] uppercase tracking-[2px] text-muted">Cost per initiative</div>
                    <a href="/initiatives" className="text-[11px] text-accent hover:underline">Edit register →</a>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-[1px] text-muted text-left">
                        <th className="font-normal pb-2">Initiative</th>
                        <th className="font-normal pb-2 text-right">Sessions</th>
                        <th className="font-normal pb-2 text-right">Hours</th>
                        <th className="font-normal pb-2 text-right">Cost</th>
                        <th className="font-normal pb-2 text-right">Goal</th>
                        <th className="font-normal pb-2 text-right">Marathons</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.initiativeBreakdown.map((r: InitiativeBreakdownRow) => {
                        const unattributed = r.initiativeSlug === '(unattributed)';
                        return (
                          <tr key={r.initiativeSlug}
                            className={`border-t border-paper-100/5 ${unattributed ? 'text-muted italic' : 'text-paper-100/90'}`}>
                            <td className="py-1.5 pr-3">
                              {r.title ?? r.initiativeSlug}
                              {r.status && !unattributed && (
                                <span className="ml-2 text-[10px] text-muted">· {r.status}</span>
                              )}
                            </td>
                            <td className="py-1.5 text-right tabular-nums">{r.sessions}</td>
                            <td className="py-1.5 text-right tabular-nums">{r.hours == null ? '—' : Math.round(r.hours)}</td>
                            <td className="py-1.5 text-right tabular-nums">{usd(r.recordedCostUsd)}</td>
                            <td className="py-1.5 text-right tabular-nums">{pct(r.goalPct == null ? null : r.goalPct / 100)}</td>
                            <td className="py-1.5 text-right tabular-nums">{r.marathonSessions}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {/* @block 2.9 Tool friction (latest day) */}
              {latest.toolErrorsTop?.length ? (
                <div className="rounded-xl border border-paper-100/10 bg-ink-900 p-5">
                  <div className="text-[11px] uppercase tracking-[2px] text-muted mb-3">
                    Tool friction · {latest.day}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {latest.toolErrorsTop.map((t) => (
                      <div key={t.tool} className="text-xs">
                        <div className="text-paper-100/80 truncate" title={t.tool}>{t.tool}</div>
                        <div className="text-muted">{t.errors}/{t.calls} · {t.err_pct}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
